import React from "react";
import { Link } from "react-router-dom";
import { Music2, Users, Sparkles, Heart, Bell, Star } from "lucide-react";
import { Reveal, Eyebrow } from "./Decorative";
import { TROUPE_PHOTO, INTEREST_HREF } from "../lib/constants";
import { ROUTES } from "../lib/seo";
import { trackNotifyClick } from "../lib/track";
import { useUpcomingShows } from "../lib/shows";
import { SHOWS_SO_FAR } from "../lib/editions";
import { EVENT_TITLE } from "../data/shows";
import { CityBookButtons } from "./Shows";
import { byFile, src, srcSet, webpSrcSet } from "./../lib/gallery";

/* ============================================================================
   "WHAT IS BHAJAN JAMMING?" — explainer for cold traffic
   ============================================================================ */
const POINTS = [
  {
    icon: Music2,
    title: "Live bhajans you sing along to",
    body: "Traditional bhajans, sufi, and devotional music — performed live and reimagined with modern production. You don't just listen. You sing.",
  },
  {
    icon: Users,
    title: "All ages, all voices",
    body: "Grandparents, parents, children — Antara evenings draw a multi-generational room that comes to sing, not to spectate.",
  },
  {
    icon: Sparkles,
    title: "Sacred energy, modern setting",
    body: "Premium audio, immersive lighting, a proper auditorium. Not a temple. Not a club. Somewhere truly special in between.",
  },
];

export function WhatIsBhajanJamming() {
  const shows = useUpcomingShows();
  return (
    <section id="what-is-it" className="bg-ivory py-24 md:py-32 px-5" data-testid="what-is-section">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow>First time hearing about it?</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-[56px] leading-[1.05] text-ink">
              What is <span className="gold-grad italic">Bhajan Jamming?</span>
            </h2>
            <p className="mt-6 font-body text-base md:text-lg text-ink-2/85 leading-relaxed">
              Antara is a live bhajan experience where the audience becomes the choir.
              Think Coke Studio meets your grandmother's evening aarti — except you're part of it.{" "}
              <Link to={ROUTES.pillar.path} className="text-amber underline underline-offset-4 decoration-gold/50 hover:text-gold transition">
                Read the full explainer
              </Link>.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-3 gap-5 md:gap-6">
          {POINTS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.12}>
              <div className="card-lift h-full p-7 md:p-8 rounded-2xl bg-ivory-2 border border-gold/25" data-testid={`what-card-${i}`}>
                <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#E5C880] to-[#B8923E] shadow-[0_8px_22px_-6px_rgba(184,146,62,0.5)]">
                  <p.icon size={24} strokeWidth={1.4} color="#2A1F17" />
                </div>
                <h3 className="mt-5 font-display text-2xl text-ink leading-tight">{p.title}</h3>
                <p className="mt-3 font-body text-sm md:text-base text-ink-2/85 leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Mid-funnel CTA: the booking while a show is ahead, else the interest list */}
        <Reveal delay={0.2}>
          {shows.length > 0 ? (
          <div className="mt-16 mx-auto max-w-2xl text-center">
            <CityBookButtons shows={shows} placement="mid" />
          </div>
          ) : (
          <div className="mt-16 text-center">
            <a
              href={INTEREST_HREF}
              onClick={trackNotifyClick}
              className="cta-primary"
              data-testid="what-is-interest-btn"
            >
              <Bell size={18} />
              <span>Tell us your city</span>
              <span className="cta-arrow">→</span>
            </a>
            <p className="mt-3 font-body text-sm font-medium text-amber">We'll let you know when Antara comes to you</p>
          </div>
          )}
        </Reveal>
      </div>
    </section>
  );
}

/* ============================================================================
   TRUST BAR — quick credibility row
   ============================================================================ */
const TRUST = [
  { num: String(SHOWS_SO_FAR), label: "shows so far" },
  { num: "~1,500", label: "in a single day" },
  { num: "2 hrs", label: "of live bhajan" },
  { num: "Sold out", label: "both October shows" },
];

export function TrustBar() {
  return (
    <section className="py-10 md:py-14 px-5 border-y border-gold/15" style={{ background: "linear-gradient(135deg, #FBF6EE 0%, #F2EAD9 100%)" }} data-testid="trust-bar">
      <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
        {TRUST.map((t, i) => (
          <Reveal key={t.label} delay={i * 0.08}>
            <div>
              <p className="font-display text-3xl md:text-4xl gold-grad">{t.num}</p>
              <p className="mt-1 font-body text-xs md:text-sm tracking-wide text-ink-2/75 uppercase">{t.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ============================================================================
   MEET ANTARA — full-bleed troupe image (brand text baked in)
   ============================================================================ */
export function MeetAntara() {
  return (
    <section className="relative w-full bg-[#0F0A07] py-12 md:py-20 px-4 md:px-6" data-testid="meet-antara-section">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="relative rounded-2xl md:rounded-3xl overflow-hidden border border-[#C9A961]/25 shadow-[0_40px_120px_-30px_rgba(229,200,128,0.35)]">
            <img
              src={TROUPE_PHOTO}
              alt="Antara — Devotion. Music. Connection. The live bhajan jamming troupe."
              width="1600"
              height="1600"
              loading="lazy"
              decoding="async"
              className="w-full h-auto block"
            />
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 md:mt-10 text-center font-serif-italic text-base md:text-xl px-4" style={{ color: "#E5C880" }}>
            The voices, hands, and hearts behind every Antara evening.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ============================================================================
   FINAL CTA BLOCK — sticky-feeling closer (above footer)
   ============================================================================ */
export function FinalCTA() {
  const shows = useUpcomingShows();
  return (
    <section
      className="relative py-24 md:py-36 px-5 overflow-hidden"
      style={{ background: "radial-gradient(circle at 50% 50%, #2A1F17 0%, #1A1410 70%, #0B0703 100%)" }}
      data-testid="final-cta-section"
    >
      {/* Background photo behind */}
      <div className="absolute inset-0 opacity-25">
        <img src={src(byFile("delhi-2026-08-009"), 1200)} alt="" width="1200" height="800" loading="lazy" decoding="async" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,10,7,0.6), rgba(15,10,7,0.85))" }} />
      </div>

      {/* Gold dust ambience */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: 2 + Math.random() * 2,
              height: 2 + Math.random() * 2,
              background: "#E8C277",
              boxShadow: "0 0 10px 3px rgba(232,194,119,0.6)",
              animation: `twinkle ${4 + Math.random() * 4}s ease-in-out ${Math.random() * 4}s infinite`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-3xl mx-auto text-center">
        <Reveal>
          <Star size={20} style={{ color: "#E5C880" }} className="mx-auto mb-4" />
          <p className="eyebrow" style={{ color: "#E5C880" }}>Next editions</p>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]" style={{ color: "#FBF6EE" }}>
            Don't just hear bhajans.<br />
            <span className="italic gold-grad">Sing them, with us.</span>
          </h2>
          <p className="mt-6 font-body text-base md:text-lg leading-relaxed" style={{ color: "#E8D9C5" }}>
            {shows.length
              ? `${EVENT_TITLE} is next, in ${shows.map((s) => s.city).join(" and ")}. Pick your city. Come sing.`
              : "Gurgaon, Delhi and Chandigarh have had their evenings. Tell us where you are, and we'll bring the next one closer to you."}
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-col items-center gap-4">
            {shows.length > 0 ? (
              <>
                <CityBookButtons shows={shows} placement="final" className="w-full max-w-2xl" />
                <a
                  href={INTEREST_HREF}
                  onClick={trackNotifyClick}
                  className="mt-2 font-body text-sm underline underline-offset-4 hover:opacity-80 transition"
                  style={{ color: "#E5C880" }}
                  data-testid="final-interest-btn"
                >
                  Not in {shows.map((s) => s.city).join(" or ")}? Tell us your city
                </a>
              </>
            ) : (
              <a
                href={INTEREST_HREF}
                onClick={trackNotifyClick}
                className="cta-primary"
                data-testid="final-interest-btn"
                style={{ fontSize: "18px", padding: "22px 48px" }}
              >
                <Bell size={22} />
                <span>Tell us your city</span>
                <span className="cta-arrow">→</span>
              </a>
            )}
            <div className="flex items-center gap-5 mt-4 font-body text-xs md:text-sm" style={{ color: "#D4B57A" }}>
              <span className="flex items-center gap-1.5"><Heart size={13} /> All ages welcome</span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
